(() => {
  const cache = new Map();
  const queue = [];
  let active = 0;
  const pump = () => {
    while (active < 4 && queue.length) {
      const { task, resolve, reject } = queue.shift();
      active++;
      Promise.resolve().then(task).then(resolve, reject).finally(() => { active--; pump(); });
    }
  };
  const schedule = task => new Promise((resolve, reject) => { queue.push({task, resolve, reject}); pump(); });
  const urlFor = (handle, variantId) => {
    const url = new URL(`${Shopify.routes.root}products/${encodeURIComponent(handle)}`, location.origin);
    url.searchParams.set('section_id', 'library-product-card');
    url.searchParams.set('variant', variantId);
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    else if (Shopify.theme?.role === 'unpublished') url.searchParams.set('preview_theme_id', String(Shopify.theme.id));
    return url;
  };
  async function cardHTML(handle, variantId, productId, mode) {
    if (!handle) throw new Error('Product unavailable');
    const url = urlFor(handle, variantId);
    const key = `${url.href}|${productId}|${mode}`;
    if (!cache.has(key)) {
      const promise = schedule(async () => {
        const abort = new AbortController();
        const timeout = setTimeout(() => abort.abort(), 15000);
        try {
          const response = await fetch(url, { signal: abort.signal });
          if (!response.ok) throw new Error('Card unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const card = doc.querySelector('article.dac-product-card');
          const saved = card?.querySelector('.swym-button');
          const purchase = card?.querySelector('input[name="id"]');
          if (!card || saved?.dataset.productId !== String(productId)) throw new Error('Saved product unavailable');
          const renderedVariant = saved.dataset.variantId;
          const metadata = doc.querySelector('[data-library-product-id]');
          let variantIds;
          try { variantIds = JSON.parse(metadata?.dataset.libraryVariantIds); } catch (error) {}
          const unavailable = mode === 'restock' && metadata?.dataset.libraryProductId === String(productId) &&
            /^[1-9]\d*$/.test(String(variantId)) &&
            Array.isArray(variantIds) && variantIds.length > 0 && variantIds.length < 250 &&
            variantIds.every(id => /^\d+$/.test(String(id))) &&
            variantIds.some(id => String(id) === renderedVariant) &&
            !variantIds.some(id => String(id) === String(variantId));
          if ((!unavailable && renderedVariant !== String(variantId)) ||
              (purchase && purchase.value !== renderedVariant)) throw new Error('Saved variant unavailable');
          card.querySelectorAll('script').forEach(script => script.remove());
          return { html: card.outerHTML, unavailable };
        } finally { clearTimeout(timeout); }
      });
      cache.set(key, promise);
      promise.catch(() => cache.delete(key));
      if (cache.size > 160) cache.delete(cache.keys().next().value);
    }
    return cache.get(key);
  }
  function announce(root) {
    document.dispatchEvent(new CustomEvent('dac:cards-added'));
    if (window.validateAlreadyPurchased) window.validateAlreadyPurchased();
  }
  async function render(options) {
    const {handle, variantId, productId, title, mode, sku, purchased, onRemove} = options;
    const copy = window.DACLibraryCopy;
    let card, unavailable = false;
    try {
      if (mode === 'purchased' && options.cardFactory) card = options.cardFactory();
      else {
        const result = await cardHTML(handle, variantId, productId, mode);
        unavailable = result.unavailable;
        card = new DOMParser().parseFromString(result.html, 'text/html').querySelector('article.dac-product-card');
      }
    } catch (error) {
      card = document.createElement('article');
      card.className = 'dac-library-card-error';
      const link = document.createElement(handle && !options.cardFactory ? 'a' : 'span');
      if (handle && !options.cardFactory) { const url = urlFor(handle, variantId); url.searchParams.delete('section_id'); link.href = url.href; }
      link.textContent = title;
      const message = document.createElement('p'); message.textContent = copy.card_error;
      const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = copy.retry;
      retry.onclick = async () => {
        retry.disabled = true;
        const replacement = await render(options);
        if (card.isConnected) { card.replaceWith(replacement); announce(); document.dispatchEvent(new CustomEvent('dac:library-card-retried')); }
      };
      card.append(link, message, retry);
      card.id = `product-${productId}`;
    if (mode === 'restock') card.dataset.restockProduct = productId;
      if (mode === 'restock') addRestockRemoval(card, card, options);
      return card;
    }
    card.id = `product-${productId}`;
    if (mode === 'restock') card.dataset.restockProduct = productId;
    card.dataset.libraryVariant = variantId;
    if (unavailable) {
      card.dataset.libraryUnavailable = 'true';
      card.querySelectorAll('.product-card-header, .product-card-container-content-info, [data-product-variant-sku]').forEach(element => element.remove());
      card.querySelectorAll('.product-card-container-content-footer > :not(.product-card__buttons-container)').forEach(element => element.remove());
      const url = urlFor(handle, variantId);
      url.searchParams.delete('section_id'); url.searchParams.delete('variant');
      card.querySelectorAll('a[href]').forEach(link => { link.href = url.href; });
      const message = document.createElement('p');
      message.className = 'dac-library-version-unavailable';
      message.textContent = copy.version_unavailable;
      card.querySelector('.product-card-container-content-title').after(message);
    }
    const indicator = card.querySelector('[data-already-purchased-indicator]');
    if (indicator) {
      indicator.dataset.productVariantSku = sku || '';
      indicator.classList.toggle('hidden', !purchased);
    }
    let actions = card.querySelector('.product-card__buttons-container');
    if (!actions) {
      actions = document.createElement('div');
      actions.className = 'product-card__buttons-container dac-library-actions';
      card.querySelector('.product-card-container-content').append(actions);
    }
    const original = actions.querySelector('.product-card-container-content-footer-btn');
    const actionClass = original?.className || 'product-card-container-content-footer-btn';
    actions.replaceChildren();
    if (mode === 'purchased') {
      const button = document.createElement('button'); button.type = 'button'; button.className = actionClass;
      button.dataset.alreadyPurchasedRemoveButton = '';
      button.textContent = copy.remove;
      button.setAttribute('aria-label', copy.remove_named.replace('__TITLE__', title));
      const error = document.createElement('p'); error.className = 'dac-library-action-error'; error.setAttribute('role','status');
      button.onclick = async () => {
        button.disabled = true; button.textContent = copy.removing; error.textContent = '';
        try { await onRemove(card); }
        catch (failure) { error.textContent = copy.remove_error; button.disabled = false; button.textContent = copy.remove; }
      };
      actions.append(button, error);
    } else {
      const link = document.createElement('a'); link.className = actionClass;
      const url = urlFor(handle, variantId); url.searchParams.delete('section_id');
      if (unavailable) url.searchParams.delete('variant');
      link.href = url.href; link.textContent = unavailable ? copy.view_current_kit : copy.view_product;
      actions.append(link);
      addRestockRemoval(actions, card, options);
    }
    return card;
  }
  function addRestockRemoval(actions, card, options) {
    const copy = window.DACLibraryCopy;
    const button = document.createElement('button'); button.type = 'button';
    button.className = 'swym-hide-product dac-library-remove';
    button.dataset.productId = options.productId; button.dataset.productName = options.title;
    button.textContent = copy.remove_alert;
    button.setAttribute('aria-label', copy.remove_alert_named.replace('__TITLE__', options.title));
    const error = document.createElement('p'); error.className = 'dac-library-action-error'; error.setAttribute('role', 'status');
    button.onclick = async () => {
      if (button.disabled) return;
      button.disabled = true; button.textContent = copy.removing; error.textContent = '';
      try {
        if (typeof options.onRemove !== 'function') throw new Error('Removal unavailable');
        await options.onRemove(card);
      } catch (_) {
        error.textContent = copy.remove_error; button.disabled = false; button.textContent = copy.remove_alert;
      }
    };
    actions.append(button, error);
  }
  function openModal(modal, opener = document.activeElement, options = {}) {
    const dialog = modal.matches('[role="dialog"]') ? modal : modal.querySelector('[role="dialog"]');
    if (!dialog) return;
    const previousOverflow = document.body.style.overflow;
    const previousInert = new Map();
    const isolate = () => {
      const visit = parent => [...parent.children].forEach(node => {
        if (node === modal) return;
        if (node.contains(modal)) { visit(node); return; }
        if (previousInert.has(node)) return;
        previousInert.set(node, node.inert);
        node.inert = true;
      });
      visit(document.body);
    };
    const focusable = () => [...dialog.querySelectorAll('button,input,select,a[href],[tabindex]')]
      .filter(node => !node.disabled && node.tabIndex >= 0 && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
    const focusFirst = () => (focusable()[0] || dialog).focus();
    const onFocus = event => { if (!dialog.contains(event.target)) focusFirst(); };
    const onKey = event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); modal.dismiss(); return; }
      if (event.key !== 'Tab') return;
      const items = focusable(), first = items[0], last = items.at(-1);
      if (!items.length) { event.preventDefault(); dialog.focus(); }
      else if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    };
    const observer = new MutationObserver(isolate);
    let closed = false;
    const finish = requestClose => {
      if (closed) return;
      closed = true;
      observer.disconnect();
      document.removeEventListener('focusin', onFocus, true);
      document.removeEventListener('keydown', onKey, true);
      previousInert.forEach((value, node) => { node.inert = value; });
      document.body.style.overflow = previousOverflow;
      if (!options.persistent) modal.remove();
      if (requestClose) options.onClose?.();
      if (opener?.isConnected) opener.focus();
      else {
        const heading = document.querySelector('h1');
        if (heading) {
          const tabindex = heading.getAttribute('tabindex');
          heading.tabIndex = -1;
          heading.focus();
          if (tabindex === null) heading.removeAttribute('tabindex');
          else heading.setAttribute('tabindex', tabindex);
        }
      }
    };
    modal.dismiss = () => finish(true);
    if (!options.persistent) document.body.append(modal);
    isolate();
    observer.observe(document.body, { childList: true, subtree: Boolean(options.persistent) });
    document.body.style.overflow = 'hidden';
    document.addEventListener('focusin', onFocus, true);
    document.addEventListener('keydown', onKey, true);
    focusFirst();
    return { release: () => finish(false) };
  }
  document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector('.dac-library-page');
    if (!root) return;
    root.addEventListener('change', event => {
      const input = event.target;
      if (!input.matches('input.filter-checkbox:not(.filter-multiselect)') || !input.checked) return;
      root.querySelectorAll('input.filter-checkbox').forEach(other => {
        if (other !== input && other.name === input.name) other.checked = false;
      });
    }, true);
    const grid = root.querySelector('.js-products-grid');
    const summary = root.querySelector('.dac-library-count');
    if (grid && summary) {
      const update = () => {
        // Server loading markup and the purchased controller own pending announcements.
        if (document.querySelector('#dac-purchased-config') && grid.getAttribute('aria-busy') === 'true') return;
        const count = grid.querySelectorAll(':scope > [id^="product-"]').length;
        const loading = Boolean(grid.querySelector('article.animate-pulse, .dac-card-skeleton')) || (root.dataset.restockLibrary === 'true' && grid.getAttribute('aria-busy') === 'true');
        summary.textContent = loading ? window.DACLibraryCopy.loading : (count === 1 ? window.DACLibraryCopy.showing_one : window.DACLibraryCopy.showing).replace('__COUNT__', count);
        grid.setAttribute('aria-busy', String(loading));
      };
      new MutationObserver(update).observe(grid, { childList: true });
      update();
    }
    const drawer = root.querySelector('#dac-collection-filters');
    if (!drawer) return;
    drawer.querySelector('.collection-filters > div')?.classList.add('dac-filter-summary');
    const media = matchMedia('(max-width:1279px)');
    let wasOpen = false, opener;
    const update = () => {
      const open = media.matches && drawer.classList.contains('translate-y-0');
      drawer.inert = media.matches && !open;
      document.documentElement.classList.toggle('dac-drawer-open',open);
      if(open){ drawer.setAttribute('role','dialog'); drawer.setAttribute('aria-modal','true'); drawer.setAttribute('aria-labelledby','dac-drawer-title'); }
      else { drawer.removeAttribute('role'); drawer.removeAttribute('aria-modal'); }
      if(open && !wasOpen){ opener=document.activeElement; drawer.querySelector('.dac-filter-close')?.focus(); }
      if(!open && wasOpen) opener?.focus();
      wasOpen=open;
    };
    new MutationObserver(update).observe(drawer,{attributes:true,attributeFilter:['class']});
    media.addEventListener('change',update); update();
    drawer.addEventListener('keydown',e=>{
      if(!wasOpen)return;
      if(e.key==='Escape'){e.preventDefault();drawer.querySelector('.dac-filter-close')?.click();}
      if(e.key!=='Tab')return;
      const items=[...drawer.querySelectorAll('button,input,select,a[href]')].filter(el=>!el.disabled&&el.getClientRects().length&&getComputedStyle(el).visibility!=='hidden');
      const first=items[0],last=items.at(-1);
      if(e.shiftKey&&document.activeElement===first){e.preventDefault();last?.focus();}
      else if(!e.shiftKey&&document.activeElement===last){e.preventDefault();first?.focus();}
    });
  });
  window.DACLibraryCards = { render, announce, openModal };
})();

